<template>
  <div
    v-if="!shouldShowForm"
    class="rounded border-static-default-low mb-24 flex items-center gap-16 border border-solid p-16"
  >
    <div class="flex-1">
      {{ i18n(translations.infoText) }}
    </div>

    <div class="flex gap-8">
      <RevButton
        class="mb-2 w-full md:mb-0 md:w-auto"
        size="small"
        :to="{ name: ROUTE_NAMES.OPTIONS.SHIPPING }"
        variant="primary"
      >
        {{ i18n(translations.manageShippingButton) }}
      </RevButton>
      <RevButton
        class="w-full md:w-auto"
        size="small"
        variant="secondary"
        @click="shouldShowForm = true"
      >
        {{ i18n(translations.addShippingButton) }}
      </RevButton>
    </div>
  </div>

  <RevCard v-else class="p-24">
    <div class="mb-4 flex items-center">
      <h3 class="heading-3">{{ i18n(translations.addShippingFormTitle) }}</h3>
    </div>
    <div class="mb-4 flex">
      <IconInfo class="mr-2 shrink-0" />
      <span> {{ i18n(translations.addShippingFormDescription) }}</span>
    </div>

    <Form
      :initialValues
      :isEditing="false"
      :isSubmitting
      :should-show-market-select="true"
      @cancel="handleCancel"
      @submit="handleSubmit"
    />
  </RevCard>
</template>

<script setup lang="ts">
import { type Error, getApiErrorMessage } from '#imports'
import { ref } from 'vue'

import { merchantsAPI } from '@backmarket/http-api'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevButton } from '@ds/components/Button'
import { RevCard } from '@ds/components/Card'
import { IconInfo } from '@ds/icons/IconInfo'

import { ROUTE_NAMES } from '@/constants/routes'
import { DEFAULT_INITIAL_VALUES } from '@/scopes/options/pages/tabs/ShippingLegacy/components/Form/Form.constants'
import type { FormValues } from '@/scopes/options/pages/tabs/ShippingLegacy/components/Form/Form.types'
import Form from '@/scopes/options/pages/tabs/ShippingLegacy/components/Form/Form.vue'

import translations from '../Shipping.translations'

const emit = defineEmits(['new-method'])
const props = defineProps<{
  listingId: number
}>()

const { openErrorToast, openSuccessToast } = useTheToast()
const i18n = useI18n()

const initialValues = ref<FormValues>({
  ...DEFAULT_INITIAL_VALUES,
})
const shouldShowForm = ref(false)
const isSubmitting = ref(false)

function handleCancel() {
  shouldShowForm.value = false
}

async function handleSubmit(formValues: FormValues) {
  isSubmitting.value = true
  try {
    await $httpFetch(merchantsAPI.addListingShippingMethod, {
      body: {
        delay: formValues.shippingDelay,
        price: formValues.cost,
        carrier: formValues.carrierId,
      },
      pathParams: {
        listingId: props.listingId,
        market: formValues.market,
      },
    })

    openSuccessToast({
      title: i18n(translations.toastCreationSuccessTitle),
      content: i18n(translations.toastCreationSuccessContent),
    })
    emit('new-method')
  } catch (error) {
    openErrorToast({
      content: getApiErrorMessage(error as unknown as Error),
    })
  } finally {
    isSubmitting.value = false
  }
}
</script>
